import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Calendar, MapPin, Mountains, ArrowRight, Path } from "@phosphor-icons/react";
import { formatEventDate } from "@/utils/dateUtils";
import { HighlightBadge } from "@/components/HighlightBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const { t, lang } = useTranslation();
  const { formatPrice } = useCurrency();

  useEffect(() => { axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {}); }, []);

  const localeMap = { es: "es-CL", en: "en-US", pt: "pt-BR", fr: "fr-FR" };
  const formatDate = (d) => formatEventDate(d, localeMap[lang] || "es-CL", { day: "numeric", month: "long", year: "numeric" });
  const diffColors = { principiante: "text-green-400 border-green-400/30", intermedio: "text-yellow-400 border-yellow-400/30", avanzado: "text-red-400 border-red-400/30" };
  const fromLabel = { es: "Desde", en: "From", fr: "A partir de", pt: "A partir de" };

  return (
    <main className="pt-20 min-h-screen">
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-2">Pro Vertical</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-black uppercase font-['Cabinet_Grotesk'] mb-4" data-testid="events-page-title">{t("events_title")}</h1>
          <p className="text-base text-gray-400 max-w-lg">{t("events_desc")}</p>
        </div>
      </section>

      <section className="pb-24" data-testid="events-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }} className="space-y-6">
            {events.map(ev => {
              const distPrices = (ev.distances || []).map(d => Number(d.price || 0));
              const hasDistances = distPrices.length > 0;
              const minPrice = hasDistances ? Math.min(...distPrices) : Number(ev.price || 0);
              const isFree = minPrice === 0;
              return (
              <motion.div key={ev.id} variants={fadeUp}>
                <Link to={`/eventos/${ev.id}`} className="block" data-testid={`event-card-${ev.id}`}>
                  <div className="bg-[#121212] border border-white/5 overflow-hidden card-hover flex flex-col md:flex-row group cursor-pointer">
                    <div className="md:w-80 h-56 md:h-auto flex-shrink-0 overflow-hidden relative">
                      <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <HighlightBadge text={ev.badge_text} color={ev.badge_color} />
                      {isFree && (
                        <span
                          data-testid="free-event-badge"
                          className="absolute top-2 right-2 bg-[#16A34A] text-white text-[10px] sm:text-xs font-black uppercase tracking-wider px-2.5 py-1 shadow-lg shadow-black/30 z-10 pointer-events-none"
                        >
                          {t("free_label")}
                        </span>
                      )}
                    </div>
                    <div className="p-6 md:p-8 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <Badge variant="outline" className={`text-xs ${diffColors[ev.difficulty] || ""}`}>{ev.difficulty}</Badge>
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-white/5 border border-white/10 px-2.5 py-1">
                            <Calendar size={14} className="text-[#FF4D00]" />
                            {formatDate(ev.date)}
                            {ev.start_time && <span className="text-[#FF4D00]"> · {ev.start_time} hrs</span>}
                          </span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-semibold mb-3 font-['Cabinet_Grotesk'] group-hover:text-[#FF4D00] transition-colors">{ev.title}</h2>
                        <p className="text-sm text-gray-400 leading-relaxed mb-4 line-clamp-2 whitespace-pre-wrap">{ev.description}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-4">
                          {ev.elevation_m && <span className="flex items-center gap-1 bg-[#1A1A1A] px-3 py-1 border border-white/5"><Mountains size={14} />{ev.elevation_m}m D+</span>}
                          <span className="flex items-center gap-1 bg-[#1A1A1A] px-3 py-1 border border-white/5"><MapPin size={14} />{ev.location}</span>
                        </div>
                        {/* Distance pills preview */}
                        {ev.distances && ev.distances.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            <span className="text-xs text-gray-500 flex items-center gap-1 mr-1"><Path size={12} /></span>
                            {ev.distances.map((dist, idx) => (
                              <span key={idx} className="px-3 py-1 bg-[#1A1A1A] border border-white/10 text-xs font-bold text-gray-300 font-['Cabinet_Grotesk']">{dist.label}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-white/5 mt-2">
                        <div>
                          {isFree ? (
                            <span className="text-xl font-bold text-[#16A34A]">{t("free_label")}</span>
                          ) : hasDistances ? (
                            <span className="text-sm text-gray-400">{fromLabel[lang] || fromLabel.es} <span className="text-lg font-bold text-[#FF4D00]">{formatPrice(minPrice)}</span></span>
                          ) : (
                            <span className="text-xl font-bold text-[#FF4D00]">{formatPrice(minPrice)}</span>
                          )}
                        </div>
                        <Button className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold" data-testid={`event-view-${ev.id}`}>
                          {t("details")} <ArrowRight size={14} className="ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
              );
            })}
          </motion.div>
          {events.length === 0 && <div className="text-center py-20 text-gray-400"><Calendar size={48} className="mx-auto mb-4 opacity-30" /><p>{t("no_events")}</p></div>}
        </div>
      </section>
    </main>
  );
}
